/**
 * Запросы к API.
 *
 * `staleTime` подобран по смыслу данных: профиль и подписка меняются редко,
 * витрина тарифов — ещё реже. Лишние запросы на мобильной сети заметны.
 */
import { useQuery } from '@tanstack/react-query';

import { apiGet, apiGetPublic } from '@/shared/api/client';
import {
  plansSchema,
  profileSchema,
  referralSchema,
  subscriptionSchema,
  type Subscription,
} from '@/shared/api/schemas';
import { isGuest } from '@/shared/lib/guest';

const MINUTE = 60_000;

/** Гость без аккаунта — то же, что пользователь без подписки. */
const GUEST_SUBSCRIPTION: Subscription = {
  state: 'no_subscription',
  days_left: null,
  has_access: false,
  device_limit_total: null,
  traffic_limit_bytes: null,
  traffic_used_bytes: 0,
  items: [],
};

export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: () => apiGet('/profile', profileSchema),
    staleTime: 5 * MINUTE,
    // Профиля у гостя нет: экран показывает приглашение войти через Telegram.
    enabled: !isGuest(),
  });
}

export function useSubscription() {
  return useQuery({
    queryKey: ['subscription'],
    queryFn: () =>
      isGuest() ? Promise.resolve(GUEST_SUBSCRIPTION) : apiGet('/subscription', subscriptionSchema),
    staleTime: MINUTE,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: ['plans'],
    queryFn: () =>
      isGuest() ? apiGetPublic('/public/plans', plansSchema) : apiGet('/plans', plansSchema),
    staleTime: 15 * MINUTE,
  });
}

export function useReferral() {
  return useQuery({
    queryKey: ['referral'],
    queryFn: () => apiGet('/referral', referralSchema),
    staleTime: MINUTE,
    enabled: !isGuest(),
  });
}
